import axios from 'axios';
import config from '../../constants/config';

/**
 * Connect (Community App) API helper.
 * Promise-based (unlike axios-helper) so screens can handle errors properly.
 * Token stored in localStorage under 'connectToken', user under 'connectUser'.
 */

const TOKEN_KEY = 'connectToken';
const USER_KEY = 'connectUser';

export const getConnectToken = () => localStorage.getItem(TOKEN_KEY);

export const getConnectUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setConnectSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const setConnectUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearConnectSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Legacy key from the localStorage-only prototype
  localStorage.removeItem('connectFollowing');
};

/**
 * POST to a connect endpoint. Resolves with response data (always has
 * .success and .response), rejects only on network failure.
 */
export const connectPost = async (endpoint, data = {}) => {
  const res = await axios.post(`${config.api.url}${endpoint}`, data, {
    crossdomain: true,
    headers: { api: config.api.key }
  });
  return res.data;
};

/**
 * Authenticated POST — token merged into payload. If the backend says the
 * token is invalid, the session is cleared and the error surfaces.
 */
export const connectAuthPost = async (endpoint, data = {}) => {
  const token = getConnectToken();
  const result = await connectPost(endpoint, { ...data, token });
  if (!result.success && /invalid or expired token/i.test(result.response || '')) {
    clearConnectSession();
  }
  return result;
};

/**
 * Upload an image (avatar | cover | post | message). Returns the response data.
 */
export const connectUploadImage = async (file, type) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('type', type);
  formData.append('token', getConnectToken());

  const res = await axios.post(`${config.api.url}connect/upload-image`, formData, {
    crossdomain: true,
    headers: { api: config.api.key }
  });
  return res.data;
};

/** Map backend snake_case user to frontend camelCase shape. */
export const normalizeUser = (u) => {
  if (!u) return null;
  return {
    id: u.id,
    fullName: u.full_name || '',
    email: u.email || '',
    gender: u.gender || '',
    birthDate: u.birth_date || '',
    countryOfResidence: u.country_of_residence || '',
    currentLocation: u.current_location || '',
    location: u.current_location || '',
    pronouns: u.pronouns || '',
    genderIdentity: u.gender_identity || '',
    sexualOrientation: u.sexual_orientation || '',
    visibility: u.visibility || 'public',
    // True when a followers-only profile withheld its details from this viewer
    restricted: !!u.restricted,
    bio: u.bio || '',
    travelStyle: u.travel_style || '',
    favoriteDestinations: Array.isArray(u.favorite_destinations) ? u.favorite_destinations : [],
    trips: Array.isArray(u.trips) ? u.trips : [],
    profilePicture: u.profile_picture || '',
    coverPhoto: u.cover_photo || '',
    isLocalGuide: !!u.is_local_guide,
    homeDestinationId: u.home_destination_id ?? null,
    onboardingComplete: !!u.onboarding_complete,
    destinationId: u.destination_id || null,
    status: u.status || 'traveler',
    openToMeet: !!u.open_to_meet,
    tagIds: Array.isArray(u.tags) ? u.tags : [],
    followers: u.followers ?? 0,
    following: u.following ?? 0,
    isFollowing: !!u.is_following,
    posts: Array.isArray(u.posts) ? u.posts.map(normalizePost) : []
  };
};

/** Map backend post to frontend shape (timestamp in ms). */
export const normalizePost = (p) => ({
  id: p.id,
  userId: p.user_id,
  content: p.content || '',
  image: p.image || null,
  location: p.location || '',
  likes: p.likes ?? 0,
  comments: p.comments ?? 0,
  likedByViewer: !!p.liked_by_viewer,
  timestamp: (p.timestamp || 0) * 1000
});

/* ------------------------------------------------------------------------
   Phase 1-3: destinations, tags, presence and meetups
   ------------------------------------------------------------------------ */

const DESTINATION_KEY = 'connectDestination';

export const getConnectDestination = () => {
  const raw = localStorage.getItem(DESTINATION_KEY);
  return raw ? parseInt(raw, 10) : null;
};

export const setConnectDestination = (id) => {
  if (id) localStorage.setItem(DESTINATION_KEY, String(id));
  else localStorage.removeItem(DESTINATION_KEY);
};

/** A person card from connect/people/discover. */
export const normalizePerson = (p) => ({
  id: p.id,
  fullName: p.full_name || '',
  profilePicture: p.profile_picture || '',
  pronouns: p.pronouns || '',
  genderIdentity: p.gender_identity || '',
  sexualOrientation: p.sexual_orientation || '',
  bio: p.bio || '',
  location: p.current_location || '',
  status: p.status || 'traveler',
  isLocalGuide: !!p.is_local_guide,
  // Derived by the server from where they live, not from their own label
  isLocal: !!p.is_local,
  homeDestinationId: p.home_destination_id ?? null,
  openToMeet: !!p.open_to_meet,
  isFollowing: !!p.is_following,
  tags: Array.isArray(p.tags) ? p.tags : [],
  trip: p.trip || null,
  availability: p.availability || null,
  reason: p.reason || ''
});

export const normalizeMeetup = (m) => ({
  id: m.id,
  title: m.title || '',
  description: m.description || '',
  area: m.area || '',
  template: m.template || '',
  destinationId: m.destination_id,
  activity: m.activity || '',
  activityTagId: m.activity_tag_id || null,
  startAt: (m.start_at || 0) * 1000,
  endAt: (m.end_at || 0) * 1000,
  capacity: m.capacity || 0,
  spacesLeft: m.spaces_left || 0,
  isFull: !!m.is_full,
  visibility: m.visibility || 'destination',
  alcoholFree: !!m.alcohol_free,
  requiresApproval: !!m.requires_approval,
  status: m.status || 'active',
  isHost: !!m.is_host,
  myStatus: m.my_status || null,
  conversationId: m.conversation_id || null,
  inChat: m.in_chat !== false,
  approvedCount: m.approved_count ?? null,
  participants: Array.isArray(m.participants) ? m.participants : null,
  host: m.host || {}
});

/** Human label for a status value. */
export const statusLabel = (status) => {
  if (status === 'local') return 'Local';
  if (status === 'returning') return 'Returning visitor';
  return 'Traveller';
};

/** "Sat 12 Oct, 18:00" */
export const formatWhen = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
    + ', ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};
